import { defineMessages } from 'react-intl'
import { EXERCISE_TYPE } from '@gymaholic/packages'

const EXERCISE_CLASS_NAME = 'Exercise'

const EXERCISE_KEY = {
  ID: 'id',
  CLASS_NAME: 'className',
  NAME: 'name',
  LEVEL: 'level',
  FORCE: 'force',
  TYPE: 'type',
  MUSCLES: 'muscles',
  MECHANICS: 'mechanics',
  INSTRUCTIONS: 'instructions',
  EQUIPMENT: 'equipment',
  SECONDARY_MUSCLES: 'secondaryMuscles',
  IMAGE: 'image',
  VIDEO: 'video',
  RECOMMENDED: 'recommended',
  ALTERNATIVES: 'alternatives',
  // Local
  LOCAL_DOWNLOADED_IMAGE: 'localDownloadedImage',
  LOCAL_DOWNLOADED_VIDEO: 'localDownloadedVideo'
}

const EXERCISE_FORCE = {
  PUSH: 1,
  PUSH_UNILATERAL: 2,
  PULL: 3,
  PULL_UNILATERAL: 4,
  ISOMETRIC: 5,
  COMPRESSION: 6
}

const label = defineMessages({
  push: {
    id: 'level.push',
    defaultMessage: 'push'
  },
  pushUnilateral: {
    id: 'level.pushUnilateral',
    defaultMessage: 'push unilateral'
  },
  pull: {
    id: 'level.pull',
    defaultMessage: 'pull'
  },
  pullUnilateral: {
    id: 'level.pullUnilateral',
    defaultMessage: 'pull unilateral'
  },
  isometric: {
    id: 'level.isometric',
    defaultMessage: 'isometric'
  },
  compression: {
    id: 'level.compression',
    defaultMessage: 'compression'
  }
})
const EXERCISE_FORCE_LABEL = {
  [EXERCISE_FORCE.PUSH]: label.push,
  [EXERCISE_FORCE.PUSH_UNILATERAL]: label.pushUnilateral,
  [EXERCISE_FORCE.PULL]: label.pull,
  [EXERCISE_FORCE.PULL_UNILATERAL]: label.pullUnilateral,
  [EXERCISE_FORCE.ISOMETRIC]: label.isometric,
  [EXERCISE_FORCE.COMPRESSION]: label.compression
}

const EXERCISE_MECHANICS = {
  COMPOUND: 1,
  ISOLATION: 2
}

const mechanicsMessages = defineMessages({
  compound: {
    id: 'level.compound',
    defaultMessage: 'compound'
  },
  isolation: {
    id: 'level.isolation',
    defaultMessage: 'isolation'
  }
})
const EXERCISE_MECHANICS_LABEL = {
  [EXERCISE_MECHANICS.COMPOUND]: mechanicsMessages.compound,
  [EXERCISE_MECHANICS.ISOLATION]: mechanicsMessages.isolation
}

const typeMessages = defineMessages({
  strength: {
    id: 'exerciseType.strength',
    defaultMessage: 'Strength',
    description: 'The label used for the strength exercise type.'
  },
  powerlifting: {
    id: 'exerciseType.powerlifting',
    defaultMessage: 'Powerlifting',
    description: 'The label used for the powerlifting exercise type.'
  },
  olympicWeightlifting: {
    id: 'exerciseType.olympicWeightlifting',
    defaultMessage: 'Olympic Weightlifting',
    description: 'The label used for the olympic weightlifting exercise type.'
  },
  strongman: {
    id: 'exerciseType.strongman',
    defaultMessage: 'Strongman',
    description: 'The label used for the strongman exercise type.'
  },
  plyometric: {
    id: 'exerciseType.plyometric',
    defaultMessage: 'Plyometric',
    description: 'The label used for the plyometric exercise type.'
  },
  cardio: {
    id: 'exerciseType.cardio',
    defaultMessage: 'Cardio',
    description: 'The label used for the cardio exercise type.'
  },
  conditioning: {
    id: 'exerciseType.conditioning',
    defaultMessage: 'Conditioning',
    description: 'The label used for the conditioning exercise type.'
  },
  stretching: {
    id: 'exerciseType.stretching',
    defaultMessage: 'Stretching',
    description: 'The label used for the stretching exercise type.'
  },
  balance: {
    id: 'exerciseType.balance',
    defaultMessage: 'Balance',
    description: 'The label used for the balance exercise type.'
  },
  smr: {
    id: 'exerciseType.smr',
    defaultMessage: 'SMR',
    description: 'The label used for the smr exercise type.'
  },
  warmup: {
    id: 'exerciseType.warmup',
    defaultMessage: 'Warmup',
    description: 'The label used for the warmup exercise type.'
  },
  yoga: {
    id: 'exerciseType.yoga',
    defaultMessage: 'Yoga',
    description: 'The label used for the yoga exercise type.'
  }
})
const EXERCISE_TYPE_LABEL = {
  [EXERCISE_TYPE.STRENGTH]: typeMessages.strength,
  [EXERCISE_TYPE.POWERLIFTING]: typeMessages.powerlifting,
  [EXERCISE_TYPE.OLYMPIC_WEIGHTLIFTING]: typeMessages.olympicWeightlifting,
  // EXERCISE_TYPE.STRONGMAN]: typeMessages.strongman,
  [EXERCISE_TYPE.PLYOMETRIC]: typeMessages.plyometric,
  [EXERCISE_TYPE.CARDIO]: typeMessages.cardio,
  [EXERCISE_TYPE.CONDITIONING]: typeMessages.conditioning,
  [EXERCISE_TYPE.STRETCHING]: typeMessages.stretching,
  [EXERCISE_TYPE.BALANCE]: typeMessages.balance,
  [EXERCISE_TYPE.SMR]: typeMessages.smr,
  [EXERCISE_TYPE.WARMUP]: typeMessages.warmup,
  [EXERCISE_TYPE.YOGA]: typeMessages.yoga
}

export {
  EXERCISE_CLASS_NAME,
  EXERCISE_MECHANICS,
  EXERCISE_TYPE_LABEL,
  EXERCISE_MECHANICS_LABEL,
  EXERCISE_FORCE,
  EXERCISE_FORCE_LABEL,
  EXERCISE_KEY,
  EXERCISE_TYPE
}

export const EXERCISE_TYPE_TO_DURATION_OR_REPS_RANGE = {
  [EXERCISE_TYPE.STRENGTH]: {
    reps: [
      8,
      12
    ]
  },
  [EXERCISE_TYPE.POWERLIFTING]: {
    reps: [
      3,
      6
    ]
  },
  [EXERCISE_TYPE.OLYMPIC_WEIGHTLIFTING]: {
    reps: [
      1,
      5
    ]
  },
  [EXERCISE_TYPE.STRONGMAN]: {
    reps: [
      3,
      8
    ]
  },
  [EXERCISE_TYPE.PLYOMETRIC]: {
    reps: [
      5,
      12
    ]
  },
  [EXERCISE_TYPE.CARDIO]: {
    time: [
      300,
      3600
    ]
  },
  // 5 min to 60 min
  [EXERCISE_TYPE.CONDITIONING]: {
    time: [
      30,
      600
    ]
  },
  // 30 sec to 10 min
  [EXERCISE_TYPE.STRETCHING]: {
    time: [
      15,
      60
    ]
  },
  // 15 sec to 1 min
  [EXERCISE_TYPE.BALANCE]: {
    time: [
      15,
      60
    ]
  },
  // 15 sec to 1 min
  [EXERCISE_TYPE.SMR]: {
    time: [
      30,
      120
    ]
  },
  // 30 sec to 2 min
  [EXERCISE_TYPE.WARMUP]: {
    time: [
      180,
      600
    ]
  },
  // 3 min to 10 min
  [EXERCISE_TYPE.YOGA]: {
    time: [
      600,
      5400
    ]
  },
  // 10 min to 90 min
  [EXERCISE_TYPE.ACTIVITY]: {
    time: [
      600,
      7200
    ]
  } // 10 min to 2 hours
}
